import { equal } from '@/api/helpers';
import { ReportRequest } from '@/client/reports';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { PerformanceOverTime } from '@/components/campaigns/analytics/PerformanceOverTime';
import { VulnerableUsersList } from '@/components/campaigns/analytics/VulnerableUsersList';
import { CampaignStatusBanner } from '@/components/dashboards/admin';
import { useGroups, useVulnerableUsersReports } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AppButton } from '@/ui';
import { FlexContainer } from '@/ui/styled-ui';
import { RiskyUsersIcon } from '@/utils/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PhishingVulnerableContainer = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  flex: 1 0 50%;
  margin-top: var(--small-padding);
  background-color: var(--green-opacity-lighter);
  padding: var(--default-padding);
  border-radius: var(--xsmall-border-radius);

  .text-container {
    cursor: pointer;
    margin-left: var(--small-padding);
    flex: 1;
    p {
      margin: 0;
    }

    .header-text {
      font-size: var(--semi-big-font-size);
      line-height: var(--semi-big-line-height);
      font-weight: 700;
      color: var(--green-chart-dark);
    }

    .description-text {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      color: var(--green-chart-dark);
    }
  }
`;

const LogoContainer = styled(FlexContainer)`
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
  background-color: var(--green-color);
  border-radius: var(--xsmall-border-radius);
  color: var(--white-main);
`;

type PhishingProductProps = {
  filters: ReportRequest;
};

export const PhishingProduct = ({ filters }: PhishingProductProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const { sort, onSort } = useTable();

  const {
    isLoading: areVulnerableUsersLoading,
    vulnerableUsers,
    isFetching: areVulnerableUsersFetching,
    total,
  } = useVulnerableUsersReports(account?.id as string, {
    ...filters,
    sort: sort && sort?.length > 0 ? [sort.join(',')] : [],
  });

  const { isLoading, groups } = useGroups({
    accountId: account?.id,
    take: 1,
    skip: 0,
    filters: [equal('name', 'Phishing Vulnerable')],
  });

  const PREVIEW_COUNT = 5;
  const filteredVulnerableUsers = vulnerableUsers?.slice(0, PREVIEW_COUNT);
  const remainingUsersCount = Math.max(0, (total || 0) - PREVIEW_COUNT);

  return (
    <>
      <CampaignStatusBanner />
      <PerformanceOverTime filters={filters} isMainDashboard />
      <VulnerableUsersList
        isFetching={areVulnerableUsersFetching}
        isLoading={areVulnerableUsersLoading}
        vulnerableUsers={filteredVulnerableUsers}
        total={total}
        isMainDashboard
        onSort={onSort}
        sort={sort}
      />
      {remainingUsersCount > 0 && !isLoading && (
        <PhishingVulnerableContainer id="phishing-vulnerable-additional-users">
          <LogoContainer>
            <RiskyUsersIcon />
          </LogoContainer>
          <FlexContainer
            className="text-container"
            direction="column"
            align="flex-start"
            justify="center"
            onClick={() =>
              !isLoading &&
              groups?.result?.[0] &&
              navigate(
                RedirectPaths[RedirectPathsEnum.GROUPS_USERS](
                  groups?.result?.[0]?.id,
                ),
              )
            }
          >
            <p className="header-text">
              {t('dashboard.phishing.vulnerable.title')}
            </p>
            <p className="description-text">
              {t('dashboard.phishing.vulnerable.description', {
                vulnerableUsersTotal: total,
              })}
            </p>
          </FlexContainer>
          <AppButton
            id="phishing-vulnerable-see-group-btn"
            severity="underline"
            label={t('dashboard.phishing.vulnerable.seeGroup')}
            isDisabled={isLoading || !groups?.result?.[0]}
            onClick={() =>
              groups?.result?.[0] &&
              navigate(
                RedirectPaths[RedirectPathsEnum.GROUPS_USERS](
                  groups?.result?.[0]?.id,
                ),
              )
            }
          />
        </PhishingVulnerableContainer>
      )}
    </>
  );
};
